// OMNI Drag & Drop Component Recipes for Chakra UI v3
// Provides consistent styling for all drag-and-drop containers and items

import { defineRecipe } from '@chakra-ui/react';

// Universal Drop Zone Recipe
export const omniDropZoneRecipe = defineRecipe({
  className: 'omni-drop-zone',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: 3,
    borderRadius: 'base',
    transition: 'all 200ms ease',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '120px',

    // Default clean styling - white background with dark text and subtle shadow
    bg: 'white',
    color: 'navbar.500', // Dark blue-gray for excellent contrast against white
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // Subtle shadow

    _hover: {
      bg: 'gray.50',
      boxShadow:
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Slightly elevated shadow on hover
    },
  },

  variants: {
    // Different container types
    variant: {
      'column-source': {
        // For ColumnsBox - clean white with subtle brand tint on hover
        bg: 'white',
        color: 'navbar.500',

        _hover: {
          bg: 'brand.50',
          boxShadow:
            '0 4px 6px -1px rgba(0, 161, 210, 0.1), 0 2px 4px -1px rgba(0, 161, 210, 0.06)', // Brand-tinted shadow
        },
      },

      'drop-target': {
        // For DimensionBox, MetricsBox - clean white with subtle brand tint on hover
        bg: 'white',
        color: 'navbar.500',

        _hover: {
          bg: 'brand.50',
          boxShadow:
            '0 4px 6px -1px rgba(0, 161, 210, 0.1), 0 2px 4px -1px rgba(0, 161, 210, 0.06)', // Brand-tinted shadow
        },
      },

      'filter-zone': {
        // For FilterBox - clean white with neutral gray tint on hover
        bg: 'white',
        color: 'navbar.500',

        _hover: {
          bg: 'gray.50',
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Neutral shadow
        },
      },
    },

    // Drop states
    dropState: {
      idle: {
        // Default state uses base styling
      },

      over: {
        bg: 'brand.50',
        boxShadow:
          '0 10px 15px -3px rgba(0, 161, 210, 0.2), 0 4px 6px -2px rgba(0, 161, 210, 0.1)', // Elevated brand shadow
        transform: 'scale(1.01)',
      },

      active: {
        bg: 'brand.100',
        boxShadow:
          '0 20px 25px -5px rgba(0, 161, 210, 0.2), 0 10px 10px -5px rgba(0, 161, 210, 0.1)', // Strong brand shadow
        transform: 'scale(1.02)',
      },
    },

    // Size variants
    size: {
      sm: {
        minHeight: '80px',
        padding: 2,
        gap: 1,
      },

      md: {
        minHeight: '120px',
        padding: 3,
        gap: 2,
      },

      lg: {
        minHeight: '160px',
        padding: 4,
        gap: 3,
      },
    },
  },

  defaultVariants: {
    variant: 'filter-zone',
    dropState: 'idle',
    size: 'md',
  },
});

// Universal Drag Item Recipe
export const omniDragItemRecipe = defineRecipe({
  className: 'omni-drag-item',

  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 2,
    margin: 1,
    borderRadius: 'md',
    cursor: 'grab',
    transition: 'all 200ms ease',
    position: 'relative',
    userSelect: 'none',

    // Unified clean styling - white background with high contrast text and subtle shadow
    bg: 'white',
    color: 'navbar.500', // Dark blue-gray for excellent contrast
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // Subtle shadow

    _hover: {
      transform: 'translateY(-1px)',
      boxShadow:
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Elevated shadow
      bg: 'gray.50',
    },

    _active: {
      cursor: 'grabbing',
      transform: 'scale(0.98)',
    },
  },

  variants: {
    // Different item types
    variant: {
      'source-column': {
        // For AthenaColumn - same clean white with subtle brand tint on hover
        bg: 'white',
        color: 'navbar.500',

        _hover: {
          bg: 'brand.50',
          transform: 'translateY(-1px)',
          boxShadow:
            '0 4px 6px -1px rgba(0, 161, 210, 0.1), 0 2px 4px -1px rgba(0, 161, 210, 0.06)', // Brand-tinted shadow
        },
      },

      'dimension-item': {
        // For DimensionItem - same clean white with subtle brand tint on hover
        bg: 'white',
        color: 'navbar.500',

        _hover: {
          bg: 'brand.50',
          transform: 'translateY(-1px)',
          boxShadow:
            '0 4px 6px -1px rgba(0, 161, 210, 0.1), 0 2px 4px -1px rgba(0, 161, 210, 0.06)', // Brand-tinted shadow
        },
      },

      'metric-item': {
        // For MetricsItem - same styling with space for actions
        bg: 'white',
        color: 'navbar.500',
        justifyContent: 'space-between',

        _hover: {
          bg: 'brand.50',
          transform: 'translateY(-1px)',
          boxShadow:
            '0 4px 6px -1px rgba(0, 161, 210, 0.1), 0 2px 4px -1px rgba(0, 161, 210, 0.06)', // Brand-tinted shadow
        },
      },

      'filter-item': {
        // For FilterItem - same clean white with neutral gray tint on hover
        bg: 'white',
        color: 'navbar.500',
        justifyContent: 'space-between',

        _hover: {
          bg: 'gray.50',
          transform: 'translateY(-1px)',
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Neutral shadow
        },
      },
    },

    // Drag states
    dragState: {
      idle: {
        opacity: 1,
      },

      dragging: {
        opacity: 0.5,
        transform: 'rotate(2deg)',
        boxShadow: 'xl',
      },

      preview: {
        opacity: 0.8,
        transform: 'scale(1.05)',
        boxShadow: '2xl',
      },
    },

    // Size variants
    size: {
      xs: {
        padding: 1,
        fontSize: 'xs',
        minHeight: '24px',
      },

      sm: {
        padding: 1.5,
        fontSize: 'sm',
        minHeight: '32px',
      },

      md: {
        padding: 2,
        fontSize: 'sm',
        minHeight: '40px',
      },

      lg: {
        padding: 3,
        fontSize: 'md',
        minHeight: '48px',
      },
    },
  },

  defaultVariants: {
    variant: 'dimension-item',
    dragState: 'idle',
    size: 'md',
  },
});
